import { Localized, useLocalization } from "@fluent/react"
import { Accordion, AccordionDetails, AccordionSummary, Alert, AppBar, Box, Button, CircularProgress, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, Tab, TableContainer, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { RichTextEditor } from "../../../app/RichTextEditor";
import { postWithAuth } from "../../../http";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { If } from "../../../app/If";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { Bug, deleteRelease, editRelease, loadBugs, loadRelease, Release } from "./ReleasesApi";
import { SaveButton } from "../../../app/components/SaveButton";
import { ReleaseDetailsButton } from "./ReleaseDetailsDialog";
import { useNavigate, useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { DeleteButton } from "../../../app/components/DeleteButton";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { Publish } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { selectSettings } from "../../settings/settingsSlice";
import { SendTestReleaseDialog } from "./SendTestReleaseDialog";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PublishReleaseDialog } from "./PublishReleaseDialog";
import { APIError } from "../../../app/appTypes";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { NotificationHistoryList } from "./NotificationHistoryList";

export const AddEditRelease = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    
    const [bugs, setBugs] = useState<Bug[]>([])
    const [originalRelease, setOriginalRelease] = useState<Release>();
    const [releaseNo, setReleaseNo] = useState<number>(0);
    const [releaseVersion, setReleaseVersion] = useState<number>(0);
    const [status, setStatus] = useState<string>("");
    const [type, setType] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [titleEN, setTitleEN] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [contentEN, setContentEN] = useState<string>("");
    const [tab, setTab] = useState<"polish" | "english">("polish");

    const [sendTestReleaseDialog, setSendTestReleaseDialog] = useState<boolean>(false);
    const [publishReleaseDialog, setPublishReleaseDialog] = useState<boolean>(false);
    const [loadingTranslation, setLoadingTranslation] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<APIError | null>(null);
    const [isError, setIsError] = useState<boolean>(false);

    const unsavedChanges = releaseVersion != originalRelease?.releaseVersion || type !== originalRelease?.releaseType || title !== originalRelease?.title || titleEN !== originalRelease?.titleEN || content !== originalRelease?.content || contentEN !== originalRelease?.contentEN;

    useEffect(() => {
        setIsLoading(true);
        if(status === "DRAFT") {
            dispatch(loadBugs()).then(unwrapResult).then(result => {
                setBugs(result);
            })
        }

        if(id !== undefined) {
            dispatch(loadRelease(id)).then(unwrapResult)
            .then(result => {
                setOriginalRelease(result);
                setReleaseNo(result.releaseNo);
                setReleaseVersion(result.releaseVersion);
                setType(result.releaseType);
                setStatus(result.status);
                setTitle(result.title);
                setTitleEN(result.titleEN);
                setContent(result.content);
                setContentEN(result.contentEN);
            })
            .catch(error => { setIsError(true); setError(error) })
            .finally(() => setIsLoading(false))
        }
    }, [id, status])

    const handleVersionChange = (event) => {
        setReleaseVersion(event.target.value);
    }

    const handleTitleChange = useCallback(e => {
        setTitle(e.target.value)
    }, [])

    const handleTitleENChange = useCallback(e => {
        setTitleEN(e.target.value)
    }, [])

    const handleTypeChange = (event) => {
        setType(event.target.value);
    }

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleSaveClick = async () => {
        setSaving(true)
        try {
            if(id !== undefined) {
                let releaseToSave ={ 
                    id, releaseType: type, title, titleEN, content, contentEN, releaseVersion
                };
        
                await dispatch(editRelease(releaseToSave)).unwrap().then(release => setOriginalRelease(release));
                
                dispatch(showSuccess("saved"));
            }
        }
        catch (error) {
                dispatch(showError("error"));
            } 
        finally {
                setSaving(false)
            }
    }

    const handleDeleteClick = async () => {
        setSaving(true)
        try {
            if(id !== undefined) {
                await dispatch(deleteRelease(id)).unwrap()
                
                dispatch(showSuccess("saved"));
                navigate("/sys/releases");
            }
        }
        catch (error) {
                dispatch(showError("error"));
            } 
        finally {
                setSaving(false)
            }
    }

    const translateEn = async () => {
        setLoadingTranslation(true)
        try {
            const result = await dispatch(postWithAuth({
                url: `api/translation/translate`,
                payload: {
                    Text: content,
                    LanguageTo: 'en-US'
                }
            }))
            
            if (postWithAuth.fulfilled.match(result)) {
                const { payload } = result
                setContentEN(payload.text)
            }
        }
        finally {
            setLoadingTranslation(false)
        }
    }

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef<Bug, any, any>[] = [
        {
            field: 'id',
            headerName: "Id",
            width: 100
        },
        {
            field: 'title',
            headerName: l10n.getString("title", null, "_Tytuł"),
            width: 200
        },
        {
            field: 'description',
            headerName: l10n.getString("description", null, "_Opis"),
            flex: 1
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            width: 200,
            valueGetter: (_, row) => row.createDate ? new Date(row.createDate).toLocaleDateString() : null
        }
    ]

    if(isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }
    
    if(isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return ( 
        <Container component={Paper} maxWidth={false} sx={{paddingTop:2, minHeight: "calc(100vh - 150px)" }}>
            <Grid container spacing={2} >
                <Grid item xs={1}>
                    <TextField 
                        value={releaseNo}
                        fullWidth
                        aria-readonly
                        label={<Localized id="lp">_Numer</Localized>} />
                </Grid>

                <Grid item xs={5}>                        
                    <FormControl fullWidth>
                        <InputLabel id="app-type-label"><Localized id="type">_Typ</Localized></InputLabel>
                        <Select
                            labelId="app-type-label"
                            value={type}
                            onChange={handleTypeChange}
                            label={<Localized id="type">_Typ</Localized>}
                            fullWidth
                        >
                            <MenuItem value={'HF'}>HF</MenuItem>
                            <MenuItem value={'RELEASE'}>RELEASE</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={3}>
                    <TextField 
                        value={releaseVersion}
                        fullWidth
                        onChange={handleVersionChange}
                        label={<Localized id="releases-version">_Wersja</Localized>} />
                </Grid>

                <Grid item xs={3}>
                    <TextField 
                        value={status}
                        fullWidth
                        aria-readonly
                        label="Status" />
                </Grid>

                <Grid item xs={6}>
                    <TextField 
                        required                            
                        value={title}
                        fullWidth
                        onChange={handleTitleChange}                         
                        label={<Localized id="releases-title">_Tytuł</Localized>} />
                </Grid>
                
                <Grid item xs={6}>
                    <TextField 
                        required                            
                        value={titleEN}
                        fullWidth
                        onChange={handleTitleENChange}                         
                        label={<Localized id="releases-title-en">_Tytuł EN</Localized>} />
                </Grid>

                <Grid item xs={12}>
                    <TabContext value={tab}>
                        <TabList onChange={handleTabChange}>
                            <Tab
                                key={0}
                                value={"polish"}
                                label={<Localized id="polish">_Polski</Localized>} />
                            <Tab
                                key={1}
                                value={"english"}
                                label={<Localized id="english">_Angielski</Localized>} />
                            <Tab
                                key={2}
                                value={"history"}
                                label={<Localized id="releases-notification-history">_Historia</Localized>}
                                disabled={id === undefined || status === "DRAFT"} />
                        </TabList>
                        <TabPanel value="polish">
                            <RichTextEditor key={content !== "" ? "loaded" : "loading"} label={<Localized id="releases-content-pl">_Treść</Localized>} value={content} onValueChange={setContent} />
                        </TabPanel>
                        <TabPanel value="english">
                            <Button
                                color="secondary"
                                onClick={() => translateEn()}
                                disabled={loadingTranslation}
                            >
                                <Localized id="translate-to-en">Przetłumacz</Localized>
                            </Button>
                            <If condition={!loadingTranslation} otherwise={<CircularProgress />}>
                                <RichTextEditor key={contentEN !== "" ? "loaded" : "loading"} label={<Localized id="releases-content-en">_Treść (EN)</Localized>} value={contentEN} onValueChange={setContentEN} />
                            </If>
                        </TabPanel>
                        <TabPanel value="history">
                            {id && <NotificationHistoryList id={Number(id)}/>}
                        </TabPanel>
                    </TabContext>
                </Grid>

                <Grid item xs={12} sx={{ marginBottom: 5 }}>
                    <If condition={status === "DRAFT"}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ marginTop: 4 }}>
                                    <Localized id="releases-resolved-bugs">_Rozwiązane błędy</Localized>
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} sx={{ marginTop: 1 }}>
                                    <DataGrid localeText={getCustomLocaleText()}
                                        style={{ minHeight: 100, height: "350px" }}
                                        rows={bugs}
                                        columns={columns}
                                        initialState={{
                                            sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                                        }}
                                    />
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    </If>
                </Grid>
            </Grid>
            
            <AppBar position="fixed" color="primary" sx={{ top: 'auto', bottom: 0, p: 0.5 }}>
                <div className="flex justify-end">
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        {unsavedChanges && <Alert variant="filled" severity="warning" sx={{ position: "absolute", left: 300, mr: 2, height: 30, py: 0, px: 1.5, fontSize: "0.75rem", alignItems: "center", '& .MuiAlert-icon': {
                            fontSize: 16
                        } }}>
                            <Localized id='unsaved-data'>_Niezapisane dane</Localized>
                        </Alert>}
                        <Stack spacing={2} direction="row">
                            <SaveButton loading={saving} onClick={handleSaveClick} />
                            {id !== undefined && (
                                <>
                                    <ReleaseDetailsButton loading={saving} id={id} disabled={unsavedChanges} />
                                    <LoadingButton
                                        loading={saving}
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<Publish />}
                                        onClick={() => setSendTestReleaseDialog(true)}
                                        disabled={unsavedChanges}>
                                        <Localized id="releases-send-test-message">_Wyślij wiadomość testową</Localized>
                                    </LoadingButton>
                                    <LoadingButton
                                        loading={saving}
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<Publish />}
                                        onClick={() => setPublishReleaseDialog(true)}
                                        disabled={unsavedChanges}>
                                        <Localized id="releases-publish">_Opublikuj</Localized>
                                    </LoadingButton>
                                </>
                            )}
                            {id !== undefined && status === 'DRAFT' && <DeleteButton loading={saving} onClick={handleDeleteClick} />}
                        </Stack>
                    </Box>
                </div>
            </AppBar>
            
            <If condition={sendTestReleaseDialog && id !== undefined}>
                <SendTestReleaseDialog id={id as string} onCancel={() => setSendTestReleaseDialog(false)}/>
            </If>

            <If condition={publishReleaseDialog && id !== undefined}>
                <PublishReleaseDialog id={id as string} onCancel={() => setPublishReleaseDialog(false)}/>
            </If>
        </Container>
    )
}